import { useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Typography from '../StyledTypography';
import Button from '../Button';

import CommentViewer from '../Comments/CommentViewer';
import styles from './AdditionalDocuments.module.css';
import CommentToggle from '../Comments/CommentToggle';
import axiosInstance from '../../../axiosInstance';
import useAdditionalDocumentsCommentsApi from '../../../hooks/api/comments/useAdditionalDocumentsCommentsApi';
import ApprovalRejectionReasonItem from '../FormApproveComponents/ApprovalRejectionReasonItem';

const AdditionalDocuments = ({
  requestId,
  requestDateEnded,
  additionalDocumentsComments,
  setAdditionalDocumentsComments,
  requestProgress,
  currentUserEmail,
}) => {
  const { t } = useTranslation();
  const [loadingComments, setLoadingComments] = useState(false);
  const [comments, setComments] = useState(null);
  const [loadingReceipts, setLoadingReceipts] = useState(false);
  const [paymentReceipts, setPaymentReceipts] = useState([]);
  const { addComment, updateComment, deleteComment } =
    useAdditionalDocumentsCommentsApi();
  const [currentUser, setCurrentUser] = useState(null);

  const canAddComment = true;

  // Determine which comments to display - for new requests
  const displayComments = additionalDocumentsComments
    ? additionalDocumentsComments
    : comments;

  const handleAddComment = useCallback(
    async ({ comment, attachedFiles }) => {
      // only for new requests
      if (additionalDocumentsComments) {
        setAdditionalDocumentsComments((prev) => [
          {
            comment: {
              comment,
              attachedFiles,
              user: currentUser,
            },
          },
          ...prev,
        ]);
        return;
      }

      if (!requestId) return null;

      try {
        const newComment = await addComment({
          comment,
          requestId,
          attachedFiles,
        });

        setComments((prev) => [newComment, ...prev]);
      } catch (error) {
        console.log('Error adding comment:', error);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [addComment, requestId, currentUser]
  );

  // Fetch user only for new requests
  useEffect(() => {
    const fetchCurrentUser = async () => {
      try {
        const response = await axiosInstance.get(`/api/users/me`);
        setCurrentUser(response?.data?.user);
      } catch (err) {
        console.error('Error fetching current user:', err);
      }
    };
    if (additionalDocumentsComments) {
      fetchCurrentUser();
    }
  }, []);

  const fetchAdditionalDocuments = useCallback(async () => {
    try {
      if (!requestId) {
        return;
      }
      setLoadingComments(true);
      const response = await axiosInstance.get(
        `/api/additional-documents-comments/${requestId}`
      );
      setComments(response.data || []);
    } catch (error) {
      console.log('Error fetching additional documents:', error);
    } finally {
      setLoadingComments(false);
    }
  }, [requestId]);

  useEffect(() => {
    fetchAdditionalDocuments();
  }, [fetchAdditionalDocuments]);

  const fetchPaymentReceipts = useCallback(async () => {
    try {
      if (!requestId) {
        setPaymentReceipts([]);
        return;
      }
      setLoadingReceipts(true);
      const response = await axiosInstance.get(
        `/api/payments/receipts/${requestId}`
      );
      setPaymentReceipts(response?.data?.data || []);
    } catch (error) {
      console.log('Error fetching payment receipts:', error);
      setPaymentReceipts([]);
    } finally {
      setLoadingReceipts(false);
    }
  }, [requestId]);

  useEffect(() => {
    fetchPaymentReceipts();
  }, [fetchPaymentReceipts]);

  const formatReceiptName = useCallback(
    (stageName, transactionId) => {
      const baseName = stageName || t('payment.receiptDefaultStageName');
      const normalizedStageName = String(baseName).trim().replace(/\s+/g, '-');
      return `${normalizedStageName}_payment_receipt_${transactionId}`;
    },
    [t]
  );

  const formatAmount = useCallback((amountCents) => {
    const amount = Number(amountCents) / 100;
    return Number.isFinite(amount) ? amount.toFixed(2) : '0.00';
  }, []);

  const handleDownloadReceipt = useCallback(
    async (receipt) => {
      try {
        const fileName = `${formatReceiptName(
          receipt.stage_name,
          receipt.transaction_id
        )}.pdf`;
        const response = await axiosInstance.get(
          `/api/payments/receipts/${receipt.transaction_id}/download`,
          { responseType: 'blob' }
        );
        const blobUrl = window.URL.createObjectURL(
          new Blob([response.data], { type: 'application/pdf' })
        );
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(blobUrl);
      } catch (error) {
        console.log('Error downloading payment receipt:', error);
      }
    },
    [formatReceiptName]
  );

  if (loadingComments) {
    return (
      <div className={styles.loadingContainer}>
        <Typography variant="bodyDefault" color="textSecondary">
          {t('loading')}
        </Typography>
      </div>
    );
  }

  return (
    <div>
      {requestDateEnded && (
        <ApprovalRejectionReasonItem requestId={requestId} status="rejected" />
      )}

      {(loadingReceipts || paymentReceipts.length > 0) && (
        <div className={styles.receiptsContainer}>
          <Typography variant="bodyDefault" className={styles.receiptsTitle}>
            {t('payment.receiptsTitle')}
          </Typography>
          {loadingReceipts ? (
            <Typography variant="bodyDefault" color="textSecondary">
              {t('loading')}
            </Typography>
          ) : (
            <div className={styles.receiptsList}>
              {paymentReceipts.map((receipt) => (
                <div
                  className={styles.receiptItem}
                  key={receipt.transaction_id}
                >
                  <div className={styles.receiptInfo}>
                    <Typography
                      variant="bodyDefault"
                      className={styles.receiptName}
                    >
                      {formatReceiptName(
                        receipt.stage_name,
                        receipt.transaction_id
                      )}
                    </Typography>
                    <Typography variant="bodySmall" color="textSecondary">
                      {formatAmount(receipt.amount_cents)} {receipt.currency}
                    </Typography>
                  </div>
                  <Button
                    text={t('payment.receiptDownload')}
                    color="primary"
                    onClick={() => handleDownloadReceipt(receipt)}
                    className={styles.receiptButton}
                  />
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {displayComments && displayComments.length > 0 && (
        <CommentViewer
          comments={displayComments}
          compact={false}
          hideHeader={false}
          requestProgress={requestProgress}
          requestDateEnded={requestDateEnded}
          isAdditionalDocument={true}
          currentUserEmail={currentUserEmail}
          requestId={requestId}
          onCommentUpdated={(updatedComment) => {
            setComments((prev) =>
              prev
                ? prev.map((c) =>
                    c.comment?.id === updatedComment.id
                      ? { ...c, comment: updatedComment }
                      : c
                  )
                : prev
            );
          }}
          onCommentDeleted={(commentId) => {
            setComments((prev) =>
              prev ? prev.filter((c) => c.comment?.id !== commentId) : prev
            );
          }}
        />
      )}

      {/* Show Add Comment button if allowed */}

      <CommentToggle
        onAddComment={handleAddComment}
        buttonText={t('add_comment')}
        hasComments={displayComments && displayComments.length > 0}
      />
    </div>
  );
};

export default AdditionalDocuments;
